import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, TrendingUp, Handshake, ArrowRight } from "lucide-react";
import { Reveal, SectionHeading } from "./Section";

const PATHS = [
  {
    id: "business-owner",
    icon: Building2,
    label: "For Business Owners",
    heading: "Are you looking for what's next?",
    items: ["Looking for a successor?", "Looking to sell your business?", "Looking for strategic capital?", "Looking to expand internationally?", "Looking for a technology partner?", "Looking to enter India or international markets?"],
    cta: "Submit Your Business",
  },
  {
    id: "investor",
    icon: TrendingUp,
    label: "For Investors",
    heading: "Are you looking for meaningful dealflow?",
    items: ["Businesses with growth potential", "Strategic acquisitions", "Technology opportunities", "India opportunities", "UAE–India opportunities", "Operating businesses", "Founder-led opportunities"],
    cta: "Submit Investment Interest",
  },
  {
    id: "partner",
    icon: Handshake,
    label: "For Strategic Partners",
    heading: "Looking to build something together?",
    items: ["Build something together", "Enter a new market", "Develop technology", "Work with governments", "Build a joint venture", "Create a long-term partnership"],
    cta: "Start a Conversation",
  },
];

const SearchFund = ({ onEnquire }) => {
  const [active, setActive] = useState(PATHS[0].id);
  const current = PATHS.find((p) => p.id === active);

  return (
    <section id="search-fund" className="relative py-28 md:py-36" data-testid="search-fund-section">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeading index="08" eyebrow="Search Fund" title="Search for impact." subtitle="Some opportunities are waiting to be discovered. If you are building a business, looking for a strategic partner, or looking to deploy capital into meaningful opportunities — let's start a conversation." />
        <Reveal>
          <div className="flex flex-wrap gap-2 rounded-full border border-white/10 bg-[#10141d] p-1.5 w-fit" data-testid="search-fund-tabs">
            {PATHS.map((p) => (
              <button key={p.id} onClick={() => setActive(p.id)} data-testid={`search-fund-tab-${p.id}`} className={`relative flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${active === p.id ? "text-[#0A0C10]" : "text-slate-300 hover:text-slate-100"}`}>
                {active === p.id && <motion.span layoutId="sf-pill" className="absolute inset-0 rounded-full bg-[#E2C08D]" transition={{ type: "spring", bounce: 0.2, duration: 0.55 }} />}
                <p.icon size={15} className="relative z-10" />
                <span className="relative z-10">{p.label}</span>
              </button>
            ))}
          </div>
        </Reveal>
        <AnimatePresence mode="wait">
          <motion.div key={active} initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.4 }} className="mt-10 rounded-2xl border border-white/8 bg-[#10141d] p-8 md:p-12" data-testid={`search-fund-panel-${active}`}>
            <h3 className="font-display text-3xl md:text-4xl font-semibold text-slate-100">{current.heading}</h3>
            <ul className="mt-8 grid gap-x-10 gap-y-4 sm:grid-cols-2">
              {current.items.map((item) => (
                <li key={item} className="flex items-start gap-3 text-slate-300">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#E2C08D]" />
                  <span className="text-base">{item}</span>
                </li>
              ))}
            </ul>
            <button onClick={() => onEnquire(active)} data-testid={`search-fund-cta-${active}`} className="group mt-10 inline-flex items-center gap-2 rounded-full bg-[#E2C08D] px-7 py-3.5 text-sm font-semibold text-[#0A0C10] hover:bg-[#f0d3a4] transition-colors">
              {current.cta} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </button>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default SearchFund;
